/**
 * Icon.js — the app's single line-icon family (24px grid, 1.75 stroke,
 * currentColor). Replaces the Unicode glyphs the nav used to render, whose
 * weight and alignment varied by platform font.
 *
 * Decorative by default (aria-hidden). Pass `label` only when the icon is the
 * sole content of something that needs a name — and prefer putting the name
 * on the button instead.
 */
import React from 'react';

const P = {
  flow:      <><circle cx="6" cy="6" r="2.5" /><circle cx="18" cy="18" r="2.5" /><path d="M6 8.5v3a3 3 0 0 0 3 3h6a3 3 0 0 1 3 3v-2" /><path d="M8.5 6H14a4 4 0 0 1 4 4v5.5" /></>,
  design:    <><rect x="3.5" y="3.5" width="17" height="17" rx="2.5" /><path d="M3.5 9h17M9 20.5V9" /></>,
  build:     <><path d="m8.5 8-4 4 4 4M15.5 8l4 4-4 4M13.5 5l-3 14" /></>,
  publish:   <><path d="M12 15.5V4M7.5 8.5 12 4l4.5 4.5" /><path d="M4.5 14v3.5a2.5 2.5 0 0 0 2.5 2.5h10a2.5 2.5 0 0 0 2.5-2.5V14" /></>,
  support:   <><circle cx="12" cy="12" r="8.5" /><circle cx="12" cy="12" r="3.5" /><path d="m6 6 3.5 3.5M14.5 14.5 18 18M18 6l-3.5 3.5M9.5 14.5 6 18" /></>,
  usage:     <><path d="M4 20h16" /><path d="M7 16.5v-5M12 16.5V6.5M17 16.5v-8" /></>,
  settings:  <><circle cx="12" cy="12" r="3" /><path d="M12 2.8v2.4M12 18.8v2.4M21.2 12h-2.4M5.2 12H2.8M18.5 5.5l-1.7 1.7M7.2 16.8l-1.7 1.7M18.5 18.5l-1.7-1.7M7.2 7.2 5.5 5.5" /></>,
  menu:      <><path d="M4 7h16M4 12h16M4 17h16" /></>,
  close:     <><path d="M6 6l12 12M18 6 6 18" /></>,
  search:    <><circle cx="11" cy="11" r="6.5" /><path d="m16 16 4 4" /></>,
  plus:      <><path d="M12 5v14M5 12h14" /></>,
  chevronRight: <><path d="m9 6 6 6-6 6" /></>,
  chevronLeft:  <><path d="m15 6-6 6 6 6" /></>,
  chevronDown:  <><path d="m6 9 6 6 6-6" /></>,
  check:     <><path d="m5 12.5 4.5 4.5L19 7.5" /></>,
  checkCircle: <><circle cx="12" cy="12" r="8.5" /><path d="m8.5 12.2 2.4 2.4 4.6-4.8" /></>,
  alert:     <><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5v5.5M12 16.2v.3" /></>,
  warning:   <><path d="M10.3 4.3 3 17a2 2 0 0 0 1.7 3h14.6A2 2 0 0 0 21 17L13.7 4.3a2 2 0 0 0-3.4 0Z" /><path d="M12 9.5v4M12 16.8v.2" /></>,
  xCircle:   <><circle cx="12" cy="12" r="8.5" /><path d="m9 9 6 6M15 9l-6 6" /></>,
  clock:     <><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" /></>,
  file:      <><path d="M14 3.5H7.5a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2V8Z" /><path d="M14 3.5V8h4.5M9 13h6M9 16.5h4" /></>,
  review:    <><rect x="3.5" y="4.5" width="17" height="13" rx="2" /><path d="M8 21h8M12 17.5V21" /><path d="m9 11 2 2 4-4" /></>,
  external:  <><path d="M14 4h6v6M20 4l-8.5 8.5" /><path d="M18 14v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4" /></>,
  refresh:   <><path d="M19.5 12a7.5 7.5 0 1 1-2.2-5.3L19.5 9" /><path d="M19.5 4v5h-5" /></>,
  more:      <><circle cx="12" cy="5.5" r="1.25" fill="currentColor" /><circle cx="12" cy="12" r="1.25" fill="currentColor" /><circle cx="12" cy="18.5" r="1.25" fill="currentColor" /></>,
  copy:      <><rect x="8.5" y="8.5" width="11.5" height="11.5" rx="2" /><path d="M15.5 8.5V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v7.5a2 2 0 0 0 2 2h2.5" /></>,
  globe:     <><circle cx="12" cy="12" r="8.5" /><path d="M3.5 12h17M12 3.5c2.4 2.3 3.5 5.2 3.5 8.5s-1.1 6.2-3.5 8.5c-2.4-2.3-3.5-5.2-3.5-8.5S9.6 5.8 12 3.5Z" /></>,
  link:      <><path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1" /><path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" /></>,
  upload:    <><path d="M12 15V4M7.5 8.5 12 4l4.5 4.5M4.5 15v3a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2v-3" /></>,
  video:     <><rect x="3" y="6" width="13" height="12" rx="2" /><path d="m16 10 5-3v10l-5-3" /></>,
  record:    <><circle cx="12" cy="12" r="8.5" /><circle cx="12" cy="12" r="3.5" fill="currentColor" /></>,
  play:      <><path d="M7 5.5v13l11-6.5Z" /></>,
  stop:      <><rect x="6" y="6" width="12" height="12" rx="1.5" /></>,
  trash:     <><path d="M4.5 6.5h15M9.5 6.5V4.5h5v2M6.5 6.5l1 13h9l1-13" /></>,
  edit:      <><path d="M4 20h4L19 9l-4-4L4 16Z" /><path d="m13.5 6.5 4 4" /></>,
  eye:       <><path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" /><circle cx="12" cy="12" r="3" /></>,
  lock:      <><rect x="5" y="10.5" width="14" height="10" rx="2" /><path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" /></>,
  signOut:   <><path d="M14 4.5h3.5a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H14" /><path d="M10 16.5 5.5 12 10 7.5M5.5 12h10" /></>,
  sparkle:   <><path d="M12 4.5 13.8 10 19.5 12l-5.7 2L12 19.5 10.2 14 4.5 12l5.7-2Z" /></>,
  plug:      <><path d="M9 3.5v4M15 3.5v4M6.5 7.5h11V11a5.5 5.5 0 0 1-11 0Z" /><path d="M12 16.5v4" /></>,
  book:      <><path d="M4.5 5.5A2 2 0 0 1 6.5 3.5H19.5v14H6.5a2 2 0 0 0-2 2Z" /><path d="M4.5 19.5a2 2 0 0 0 2 1h13v-3" /></>,
  headset:   <><path d="M4.5 14v-2a7.5 7.5 0 0 1 15 0v2" /><rect x="3.5" y="13" width="4" height="6" rx="1.5" /><rect x="16.5" y="13" width="4" height="6" rx="1.5" /><path d="M18.5 19a3 3 0 0 1-3 2.5H13" /></>,
};

export default function Icon({ name, size = 20, label, className, style }) {
  const paths = P[name];
  if (!paths) return null;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
      aria-hidden={label ? undefined : true}
      role={label ? 'img' : undefined}
      aria-label={label}
      focusable="false"
    >
      {paths}
    </svg>
  );
}
